import {
  Avatar,
  Button,
  makeStyles,
  Menu,
  MenuItem,
  MenuList,
  MenuPopover,
  MenuTrigger,
  Text,
  tokens,
} from '@fluentui/react-components';
import {
  WeatherMoonRegular,
  WeatherSunnyRegular,
  PersonRegular,
  SignOutRegular,
} from '@fluentui/react-icons';
import type { ReactNode } from 'react';

import { spacing } from '@/theme/tokens';
import { useThemeStore } from '@/theme/themeStore';

const useStyles = makeStyles({
  root: {
    display: 'flex',
    alignItems: 'center',
    gap: spacing.l,
    padding: `${spacing.s} ${spacing.xxl}`,
  },
  brand: {
    display: 'flex',
    alignItems: 'baseline',
    gap: spacing.s,
    flexShrink: 0,
  },
  brandMark: {
    display: 'inline-block',
    width: '10px',
    height: '10px',
    borderRadius: '50%',
    backgroundColor: tokens.colorBrandBackground,
  },
  spacer: {
    flexGrow: 1,
  },
  trailing: {
    display: 'flex',
    alignItems: 'center',
    gap: spacing.s,
  },
});

interface AppHeaderProps {
  title?: string;
  left?: ReactNode;
  right?: ReactNode;
  user?: { name: string; email?: string };
  onSignOut?: () => void;
}

export function AppHeader({ title = 'hub', left, right, user, onSignOut }: AppHeaderProps) {
  const classes = useStyles();
  const { themeName, toggle } = useThemeStore();

  return (
    <div className={classes.root}>
      <div className={classes.brand}>
        <span className={classes.brandMark} aria-hidden />
        <Text weight="semibold" size={500}>
          {title}
        </Text>
        <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>
          CareDocs internal
        </Text>
      </div>
      {left}
      <div className={classes.spacer} />
      {right}
      <div className={classes.trailing}>
        <Button
          appearance="subtle"
          icon={themeName === 'dark' ? <WeatherSunnyRegular /> : <WeatherMoonRegular />}
          aria-label={`Switch to ${themeName === 'dark' ? 'light' : 'dark'} theme`}
          onClick={toggle}
        />
        {user ? (
          <Menu>
            <MenuTrigger disableButtonEnhancement>
              <Button appearance="subtle" aria-label="Account menu">
                <Avatar name={user.name} size={28} />
              </Button>
            </MenuTrigger>
            <MenuPopover>
              <MenuList>
                <MenuItem icon={<PersonRegular />}>{user.name}</MenuItem>
                <MenuItem icon={<SignOutRegular />} onClick={onSignOut}>
                  Sign out
                </MenuItem>
              </MenuList>
            </MenuPopover>
          </Menu>
        ) : null}
      </div>
    </div>
  );
}
